import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { Adv2Config } from '../../components/adv2-config/adv2-config';
import { ISalesforceConfig, ISalesforceObjectMapping } from '../../components/adv2-config/adv2-config';
import { Adv2ConfigService } from '../adv2-config.service';

@Injectable({ providedIn: 'root' })
export class SalesforceMappingStoreService {
  private mappings$?: Observable<ISalesforceObjectMapping[]>;
  private mappingsSnapshot: ISalesforceObjectMapping[] = [];

  constructor(private adv2ConfigService: Adv2ConfigService) {}

  /**
   * Returns every configured mapping, fetching from the server on the first access.
   */
  getMappings(): Observable<ISalesforceObjectMapping[]> {
    if (!this.mappings$) {
      this.mappings$ = this.loadMappings();
    }
    return this.mappings$;
  }

  /** @TODO: to change to doc merge enabled entities only
   * Returns enabled Salesforce object names from the latest in-memory snapshot.
   */
  getEnabledObjectNamesSnapshot(): string[] {
    return [...new Set(
      this.mappingsSnapshot
        .filter((mapping) => Boolean(mapping?.objectName) && mapping.enabled !== false)
        .map((mapping) => mapping.objectName)
    )];
  }

  /**
   * Convenience helper that only emits enabled mappings that have a Salesforce object selected.
   */
  getEnabledMappings(): Observable<ISalesforceObjectMapping[]> {
    return this.getMappings().pipe(
      map((mappings) => mappings.filter((mapping) => Boolean(mapping?.objectName) && mapping.enabled !== false))
    );
  }

  /**
   * Clears the cached observable so the next caller will refetch from the API.
   */
  refresh(): void {
    this.mappings$ = undefined;
  }

  /**
   * Allows callers (e.g. config dialog) to prime the cache with freshly saved mappings.
   */
  primeFromConfig(config: ISalesforceConfig | null | undefined): void {
    const objects = config?.objects ?? [];
    this.mappingsSnapshot = objects;
    this.mappings$ = of(objects).pipe(shareReplay(1));
  }

  private loadMappings(): Observable<ISalesforceObjectMapping[]> {
    return this.adv2ConfigService.getConfig().pipe(
      map((exchange: Adv2Config) => exchange?.config?.salesforce?.objects ?? []),
      map((mappings) => {
        this.mappingsSnapshot = mappings;
        return mappings;
      }),
      catchError((error) => {
        console.error('Failed to load Salesforce mappings', error);
        this.mappingsSnapshot = [];
        return of([]);
      }),
      shareReplay(1)
    );
  }
}
